<template>
  <Toolbar class="p-mb-4 p-mt-4 p-p-2">
    <template #start>
      <div style="font-size: 1.4em; color: inherit" class="p-p-2">{{ $t('Markups') }}</div>
    </template>

    <template #end>
      <div class="p-p-1">
        <Button @click="saveSettings"
                :disabled="!$store.getters.isAccountantOrHigher || locationIsLoading || fetchInFlight"
                :loading="locationIsLoading || fetchInFlight"
                class="p-button-raised"
                icon="ti-save"
                :label="$t('Save')"/>
      </div>
    </template>
  </Toolbar>

  <div class="p-grid">
    <div class="p-col-12 p-sm-12 p-md-6 p-lg-4 p-mb-2">
      <fieldset class="p-fieldset p-pb-4 p-pt-2">
        <legend class="p-fieldset-legend p-p-2">{{ $t('ordersMarkupTitle') }}</legend>

        <small class="p-mt-2 p-mb-3 p-text-secondary">
          {{ $t('orderEstimateMarkupHelp') }}
        </small>

        <div class="p-mt-5">
          <div class="p-float-label">
            <InputNumber id="orders-markup-default"
                         v-model="ordersMarkup.default"
                         :min="0"
                         :max="999"
                         :useGrouping="false"
                         showButtons
                         class="settings-input"
                         inputClass="settings-input-el"
                         :locale="computedNumberInputLocale"
                         autocomplete="new-password"/>
            <label for="orders-markup-default">{{ $t('defaultMarkupPercentLabel') }} (%)</label>
          </div>
        </div>

        <div v-if="canUseAdvancedMarkup" class="p-mt-3">
          <table class="markup-table">
            <colgroup>
              <col style="width: 30%">
              <col style="width: 30%">
              <col style="width: 40%">
              <col style="width: 44px">
            </colgroup>
            <thead>
            <tr>
              <th class="theme-border-color">{{ $t('markupCostFromLabel') }}</th>
              <th class="theme-border-color">{{ $t('markupCostToLabel') }}</th>
              <th class="theme-border-color">{{ $t('markupPercentLabel') }} (%)</th>
              <th class="col-del theme-border-color"></th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(r, i) in ordersMarkup.ranges" :key="`o-${i}`">
              <td class="theme-border-color">
                <InputNumber v-model="r.f"
                             :useGrouping="false"
                             :min="0.01"
                             :max="999999"
                             :minFractionDigits="2"
                             :maxFractionDigits="2"
                             class="cell-input"
                             inputClass="cell-input-el"
                             :locale="computedNumberInputLocale"
                             autocomplete="new-password"/>
              </td>
              <td class="theme-border-color">
                <InputNumber v-model="r.t"
                             :useGrouping="false"
                             :min="0.01"
                             :max="999999"
                             :minFractionDigits="2"
                             :maxFractionDigits="2"
                             class="cell-input"
                             inputClass="cell-input-el"
                             :locale="computedNumberInputLocale"
                             autocomplete="new-password"
                             placeholder="—"/>
              </td>
              <td class="theme-border-color">
                <InputNumber v-model="r.p"
                             :useGrouping="false"
                             :min="0"
                             :max="999"
                             showButtons
                             class="cell-input cell-input--with-buttons"
                             inputClass="cell-input-el"
                             :locale="computedNumberInputLocale"
                             autocomplete="new-password"/>
              </td>
              <td class="col-del theme-border-color">
                <Button class="p-button-text p-button-danger p-button-sm"
                        icon="ti-close"
                        @click="removeMarkupRange('orders', i)"/>
              </td>
            </tr>
            </tbody>
          </table>

          <div class="p-mt-2 markup-actions">
            <Button class="p-button-text"
                    icon="ti-plus"
                    :label="$t('Add Range')"
                    @click="addMarkupRange('orders')"/>
          </div>

          <small class="p-invalid" v-if="markupErrors.orders">{{ markupErrors.orders }}</small>
          <small class="muted">{{ $t('markupEmptyToHint') }}</small>
        </div>
      </fieldset>
    </div>

    <div class="p-col-12 p-sm-12 p-md-6 p-lg-4 p-mb-2">
      <fieldset class="p-fieldset p-pb-4 p-pt-2">
        <legend class="p-fieldset-legend p-p-2">{{ $t('estimatesMarkupTitle') }}</legend>

        <small class="p-mt-2 p-mb-3 p-text-secondary">
          <!--          <i class="ti-info-alt p-mr-1"/>-->
          {{ $t('orderEstimateMarkupHelp') }}
        </small>

        <div class="p-mt-5">
          <div class="p-float-label">
            <InputNumber id="estimates-markup-default"
                         v-model="estimatesMarkup.default"
                         :min="0"
                         :max="999"
                         :useGrouping="false"
                         showButtons
                         class="settings-input"
                         inputClass="settings-input-el"
                         :locale="computedNumberInputLocale"
                         autocomplete="new-password"/>
            <label for="estimates-markup-default">{{ $t('defaultMarkupPercentLabel') }} (%)</label>
          </div>
        </div>

        <div v-if="canUseAdvancedMarkup" class="p-mt-3">
          <table class="markup-table">
            <colgroup>
              <col style="width: 30%">
              <col style="width: 30%">
              <col style="width: 40%">
              <col style="width: 44px">
            </colgroup>
            <thead>
            <tr>
              <th class="theme-border-color">{{ $t('markupCostFromLabel') }}</th>
              <th class="theme-border-color">{{ $t('markupCostToLabel') }}</th>
              <th class="theme-border-color">{{ $t('markupPercentLabel') }} (%)</th>
              <th class="col-del theme-border-color"></th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(r, i) in estimatesMarkup.ranges" :key="`e-${i}`">
              <td class="theme-border-color">
                <InputNumber v-model="r.f"
                             :useGrouping="false"
                             :min="0.01"
                             :max="999999"
                             :minFractionDigits="2"
                             :maxFractionDigits="2"
                             class="cell-input"
                             inputClass="cell-input-el"
                             :locale="computedNumberInputLocale"
                             autocomplete="new-password"/>
              </td>
              <td class="theme-border-color">
                <InputNumber v-model="r.t"
                             :useGrouping="false"
                             :min="0.01"
                             :max="999999"
                             :minFractionDigits="2"
                             :maxFractionDigits="2"
                             class="cell-input"
                             inputClass="cell-input-el"
                             :locale="computedNumberInputLocale"
                             autocomplete="new-password"
                             placeholder="—"/>
              </td>
              <td class="theme-border-color">
                <InputNumber v-model="r.p"
                             :useGrouping="false"
                             :min="0"
                             :max="999"
                             showButtons
                             class="cell-input cell-input--with-buttons"
                             inputClass="cell-input-el"
                             :locale="computedNumberInputLocale"
                             autocomplete="new-password"/>
              </td>
              <td class="col-del theme-border-color">
                <Button class="p-button-text p-button-danger p-button-sm"
                        icon="ti-close"
                        @click="removeMarkupRange('estimates', i)"/>
              </td>
            </tr>
            </tbody>
          </table>

          <div class="p-mt-2 markup-actions">
            <Button class="p-button-text"
                    icon="ti-plus"
                    :label="$t('Add Range')"
                    @click="addMarkupRange('estimates')"/>
          </div>

          <small class="p-invalid" v-if="markupErrors.estimates">{{ markupErrors.estimates }}</small>
          <small class="muted">{{ $t('markupEmptyToHint') }}</small>
        </div>
      </fieldset>
    </div>

    <div class="p-col-12 p-sm-12 p-md-6 p-lg-4">
      <fieldset class="p-fieldset p-pb-4 p-pt-2">
        <legend class="p-fieldset-legend p-p-2">{{ $t('stockItemMarkupTitle') }}</legend>

        <small class="p-mt-2 p-mb-3 p-text-secondary">
          {{ $t('stockItemMarkupHelp') }}
        </small>

        <div class="p-mt-5">
          <div class="p-float-label">
            <InputNumber id="warehouse-markup-default"
                         v-model="warehouseMarkup.default"
                         :min="0"
                         :max="999"
                         :useGrouping="false"
                         showButtons
                         class="settings-input"
                         inputClass="settings-input-el"
                         :locale="computedNumberInputLocale"
                         autocomplete="new-password"/>
            <label for="warehouse-markup-default">{{ $t('defaultMarkupPercentLabel') }} (%)</label>
          </div>
        </div>

        <div v-if="canUseAdvancedMarkup" class="p-mt-3">
          <table class="markup-table">
            <colgroup>
              <col style="width: 30%">
              <col style="width: 30%">
              <col style="width: 40%">
              <col style="width: 44px">
            </colgroup>
            <thead>
            <tr>
              <th class="theme-border-color">{{ $t('markupCostFromLabel') }}</th>
              <th class="theme-border-color">{{ $t('markupCostToLabel') }}</th>
              <th class="theme-border-color">{{ $t('markupPercentLabel') }} (%)</th>
              <th class="col-del theme-border-color"></th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="(r, i) in warehouseMarkup.ranges" :key="`w-${i}`">
              <td class="theme-border-color">
                <InputNumber v-model="r.f"
                             :useGrouping="false"
                             :min="0.01"
                             :max="999999"
                             :minFractionDigits="2"
                             :maxFractionDigits="2"
                             class="cell-input"
                             inputClass="cell-input-el"
                             :locale="computedNumberInputLocale"
                             autocomplete="new-password"/>
              </td>
              <td class="theme-border-color">
                <InputNumber v-model="r.t"
                             :useGrouping="false"
                             :min="0.01"
                             :max="999999"
                             :minFractionDigits="2"
                             :maxFractionDigits="2"
                             class="cell-input"
                             inputClass="cell-input-el"
                             :locale="computedNumberInputLocale"
                             autocomplete="new-password"
                             placeholder="—"/>
              </td>
              <td class="theme-border-color">
                <InputNumber v-model="r.p"
                             :useGrouping="false"
                             :min="0"
                             :max="999"
                             showButtons
                             class="cell-input cell-input--with-buttons"
                             inputClass="cell-input-el"
                             :locale="computedNumberInputLocale"
                             autocomplete="new-password"/>
              </td>
              <td class="col-del theme-border-color">
                <Button class="p-button-text p-button-danger p-button-sm"
                        icon="ti-close"
                        @click="removeMarkupRange('warehouse', i)"/>
              </td>
            </tr>
            </tbody>
          </table>

          <div class="p-mt-2 markup-actions">
            <Button class="p-button-text"
                    icon="ti-plus"
                    :label="$t('Add Range')"
                    @click="addMarkupRange('warehouse')"/>
          </div>

          <small class="p-invalid" v-if="markupErrors.warehouse">{{ markupErrors.warehouse }}</small>
          <small class="muted">{{ $t('markupEmptyToHint') }}</small>
        </div>
      </fieldset>
    </div>
  </div>

  <BillingFeatureNotice v-if="billingEntitlementsReady && !canUseAdvancedMarkup"
                        feature="advanced_markup"
                        bordered
                        class="p-mt-3 p-mb-3"
                        :title="$t('billing.features.advanced_markup.pageTitle')"
                        :description="$t('billing.features.advanced_markup.tooltip')"/>
</template>

<script>
import httpClient from "@/services/http.services";
import settings from "@/settings";
import showErrorsMixins from "@/mixins/showErrorsMixins";
import formatMixins from "@/mixins/formatMixins";
import billingFeaturesMixins from '@/mixins/billingFeaturesMixins'
import BillingFeatureNotice from '@/components/billing/BillingFeatureNotice.vue'

export default {
  emits: ['update-items'],
  components: { BillingFeatureNotice },
  mixins: [showErrorsMixins, formatMixins,  billingFeaturesMixins ],
  props: {
    locationData: { type: Object, default: null },
    locationIsLoading: { type: Boolean, default: false },
  },
  data() {
    return {
      fetchInFlight: false,
      // submitted: false,

      ordersMarkup: { default: 0, ranges: [] },
      estimatesMarkup: { default: 0, ranges: [] },
      warehouseMarkup: { default: 0, ranges: [] },

      markupErrors: { orders: '', estimates: '', warehouse: '' },
    }
  },
  mounted() {
    this.applySettings()
  },
  watch: {
    locationData: {
      deep: true,
      handler() {
        this.applySettings()
      }
    }
  },
  methods: {
    parseJsonMaybe(v) {
      if (!v) return null
      if (typeof v === 'object') return v
      try { return JSON.parse(String(v)) } catch (e) { return null }
    },

    applySettings() {
      const s = this.locationData || {}

      const normalizeIncoming = (value) => {
        const data = this.parseJsonMaybe(value) || {}

        return {
          default: data.default ?? 0,
          ranges: Array.isArray(data.ranges)
              ? data.ranges.map(r => ({ ...r }))
              : []
        }
      }

      this.ordersMarkup = normalizeIncoming(s.orders_markup)
      this.estimatesMarkup = normalizeIncoming(s.estimates_markup)
      this.warehouseMarkup = normalizeIncoming(s.warehouse_markup)
    },

    getMarkupModel(kind) {
      if (kind === 'orders') return this.ordersMarkup
      if (kind === 'estimates') return this.estimatesMarkup
      if (kind === 'warehouse') return this.warehouseMarkup
      throw new Error('Unknown markup kind: ' + kind)
    },

    addMarkupRange(kind) {
      const m = this.getMarkupModel(kind);
      m.ranges.push({ f: 0.01, t: null, p: 0 });
    },

    removeMarkupRange(kind, idx) {
      const m = this.getMarkupModel(kind);
      m.ranges.splice(idx, 1);
    },

    normalizeMarkup(model) {
      const def = Number(model?.default ?? 0);
      const ranges = Array.isArray(model?.ranges) ? model.ranges : []

      const out = { default: 0, ranges: [] };
      out.default = Number.isFinite(def) ? Math.max(0, Math.trunc(def)) : 0

      for (const r of ranges) {
        const f = (r?.f === '' || r?.f === null || r?.f === undefined) ? null : Number(r.f)
        const t = (r?.t === '' || r?.t === null || r?.t === undefined) ? null : Number(r.t)
        const p = (r?.p === '' || r?.p === null || r?.p === undefined) ? null : Number(r.p)

        if (!Number.isFinite(f) || f <= 0) continue;
        if (t !== null && (!Number.isFinite(t) || t <= 0)) continue;
        if (!Number.isFinite(p) || p < 0) continue;

        out.ranges.push({
          f: Number(f.toFixed(2)),
          t: (t === null) ? null : Number(t.toFixed(2)),
          p: Math.trunc(p),
        });
      }

      out.ranges.sort((a, b) => a.f - b.f);
      return out;
    },

    validateMarkup(kind) {
      const model = this.getMarkupModel(kind);
      const norm = this.normalizeMarkup(model);

      for (let i = 0; i < norm.ranges.length; i++) {
        const r = norm.ranges[i];
        if (r.t !== null && r.t <= r.f) {
          return this.$t('markupRangeToGreaterMsg', { number: i + 1 })
        }
      }

      for (let i = 1; i < norm.ranges.length; i++) {
        const prev = norm.ranges[i - 1];
        const cur = norm.ranges[i];
        if (prev.t !== null && cur.f < prev.t) {
          return this.$t('markupRangesOverlapMsg', {
            from: cur.f,
            to: prev.t
          })
        }
      }

      const openIdx = norm.ranges.findIndex(x => x.t === null);
      if (openIdx !== -1 && openIdx !== norm.ranges.length - 1) {
        return this.$t('markupOpenRangeLastMsg')
      }

      model.default = norm.default;
      model.ranges = norm.ranges.map(x => ({ ...x }));

      return '';
    },

    async saveSettings() {
      // this.submitted = true;

      const locationId = this.locationData?.id;

      if (!locationId || this.fetchInFlight) return;

      this.markupErrors.orders = this.validateMarkup('orders');
      this.markupErrors.estimates = this.validateMarkup('estimates');
      this.markupErrors.warehouse = this.validateMarkup('warehouse');

      if (this.markupErrors.orders || this.markupErrors.estimates || this.markupErrors.warehouse) {
        return false;
      }

      this.fetchInFlight = true;

      const payload = {
        location_id: locationId,
        orders_markup: this.normalizeMarkup(this.ordersMarkup),
        estimates_markup: this.normalizeMarkup(this.estimatesMarkup),
        warehouse_markup: this.normalizeMarkup(this.warehouseMarkup),
      };

      try {
        const { data, status } = await httpClient.post('location/settings-update', payload);
        if (status === 200 && data?.success) {
          this.$emit('update-items');
          this.$toast.add({ severity: 'success', detail: this.$t('Data updated'), life: settings.toastLife });
        } else {
          this.showUnknownError();
        }
      } catch (err) {
        this.showError(err);
      } finally {
        this.fetchInFlight = false;
        // this.submitted = false
      }
    },
  },
}
</script>

<style scoped lang="scss">
.settings-input {
  width: 100%;
  max-width: 280px;
  min-width: 0;
  box-sizing: border-box;
}

.settings-input :deep(.p-inputnumber) {
  width: 100%;
  max-width: 280px;
  min-width: 0;
  box-sizing: border-box;
  display: flex;
}

.settings-input :deep(.p-inputnumber-input),
.settings-input :deep(input.p-inputtext) {
  width: 100%;
  min-width: 0;
  box-sizing: border-box;
}

.settings-input :deep(.p-inputnumber-button-group) {
  flex: 0 0 auto;
}

//.p-col-12,
//.p-sm-12,
//.p-md-4,
//.p-lg-4,
//.p-fieldset {
//  min-width: 0;
//}

.p-fieldset {
  min-width: 0;
}

.markup-table {
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;
}

.markup-table th,
.markup-table td {
  padding: 10px 6px;
  border-bottom: 1px solid;
  vertical-align: middle;
}

.markup-table th {
  text-align: left;
  font-weight: 600;
  border-bottom: 1px solid;
  white-space: nowrap;
}

.col-del {
  width: 44px;
  text-align: right;
}

:deep(.cell-input .p-inputnumber-input) { width: 100%; box-sizing: border-box; }


.markup-actions {
  display: flex;
  gap: 10px;
}

.muted {
  display: block;
  opacity: .7;
  margin-top: 6px;
}
</style>
